/* eslint-disable react-hooks/exhaustive-deps */
//#region Libraries
import { useEffect, useState } from "react";
//#endregion
//#region Styles
import { Container } from "./styles";
import { SkelletonLoader } from "components/CustomTableList/components/TableListItem/styles";
//#endregion
//#region Components
import ListHeader from "./components/ListHeader/ListHeader";
import CustomTabs from "components/CustomTabs/CustomTabs";
import CustomTableList from "components/CustomTableList/CustomTableList";
import Preloader from "components/Preloader/Preloader";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import CustomButton from "components/CustomButton/CustomButton";
import Paginator from "components/Paginator/Paginator";
import NotificationNoContent from "components/NotificationNoContent/NotificationNoContent";
import ListItem from "./components/ListItem/ListItem";
import InSyncOfAcadReinforceUnprocessedButton from "./components/InSyncOfAcadReinforceUnprocessedButton/InSyncOfAcadReinforceUnprocessedButton";
import DialogNotificationLeaveViewInSync from "../../../../components/DialogNotifyLeaveView/DialogNotifyLeaveView";
//#endregion
//#region Utils
import useMainContext from "utils/context/useMainContext";
//#endregion
//#region Services
import { acadReinforcementSessionStorage } from "services/storage/acadReinforcement";
import {
	requestDeleteAcadReinforceById,
	requestGetQuantityAcadReinforce,
} from "services/acadReinforce/aboutAcadReinforce";
//#endregion
//#region Utils
import useAcadReinforceList from "views/RefuerzoAcademico/utils/hooks/useAcadReinforceList";
import useFilters from "./utils/hooks/useFilters";
import { HEADERS_TABLE_LIST, RECORDS_BY_PAGE } from "./utils/constants";
import useLoadingRequest from "components/Preloader/utils/hooks/useLoadingRequest";
import usePaginator from "components/Paginator/utils/hooks/usePaginator";
import { getTabData, getViewTabTitles } from "./utils/helpers";
import useDataRelatedToFilters from "./utils/hooks/useDataRelatedToFilters";
import useSyncOfAcadReinforceUnprocessed from "./utils/hooks/useSyncOfAcadReinforceUnprocessed";
import DialogUpdateMassiveAndPendingAcadReinforce from "../../components/DialogUpdateMassiveAndPendingAcadReinforce/DialogUpdateMassiveAndPendingAcadReinforce";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import SyncAcadReinforceUnprocessedDialog from "./components/SyncAcadReinforceUnprocessedDialog/SyncAcadReinforceUnprocessedDialog";
//#endregion

/*
NOTA: 
Si existe otro módulo con una lista, filtros y tabs, se recomienda usar la implementación del "Listado de Eventos" debido a que está mejor modularizada usando de mejor forma los hooks. 
No recomiendo seguir esta forma para el "Listado de Refuerzos", puesto que está mucho más desorganizado e imperativo, si fuese posible en un futuro recomiendo refactorizarlo y acondicionarlo tal y como está en el mencionado "Listado de Eventos".
Cuidado con la gestión de las tabs, ya que aquí la cantidad es dinámica, pero en eventos es estática.
- Manuel Rivera -
*/
const ListComponent = ({ userRole, isTutorUser, toastMessageSuccess }) => {
	const mainContext = useMainContext();
	//#region States
	const [tabData] = useState(getTabData(isTutorUser));
	const [currentTabIdx, setCurrentTabIdx] = useState(-1);
	const [titleTabViews, setTitleTabViews] = useState(null);
	const [showDialogDeleteAcadReinforce, setShowDialogDeleteAcadReinforce] =
		useState(false);
	const [
		currentAcadReinforceIdForAction,
		setCurrentAcadReinforceIdForAction,
	] = useState(0);
	const [canShowDialogLeavingView, setCanShowDialogLeavingView] = useState(false);
	//#endregion
	const loadingRequest = useLoadingRequest();
	const paginator = usePaginator();
	const filters = useFilters();
	const syncOfAcadReinforceUnprocessed = useSyncOfAcadReinforceUnprocessed({
		setCanShowDialogLeavingView,
	});
	const dialogShowUpdateMassiveAndPendingAcadReinforce = useShowDialogAlert();
	const fillTitleViews = async () => {
		const quantityAcadReinforcements =
			await requestGetQuantityAcadReinforce(filters.value);
		setTitleTabViews({ ...quantityAcadReinforcements });
		const quantityAcadReinforcementsByTab =
			quantityAcadReinforcements[tabData.fields[currentTabIdx]];
		paginator.setQuantityPages({
			quantityPages: Math.ceil(
				quantityAcadReinforcementsByTab / RECORDS_BY_PAGE
			),
			totalRecords: quantityAcadReinforcementsByTab,
		});
	};
	const { dataRelatedToFilters } = useDataRelatedToFilters();
	const acadReinforceList = useAcadReinforceList({
		loadingRequest,
		tabData,
		currentTabIdx,
		filters,
		paginator,
		fillTitleViews,
	});
	//#region Effects
	const initCurrentTabIdx = () => {
		const valueLocalStorage =
			acadReinforcementSessionStorage.currentTabIdxAcadReinforceList.get();
		if (isTutorUser) {
			const generalTabIdx = tabData.values.GENERAL;
			acadReinforcementSessionStorage.currentTabIdxAcadReinforceList.set(
				generalTabIdx
			);
			setCurrentTabIdx(generalTabIdx);
			return;
		}
		if (valueLocalStorage) {
			setCurrentTabIdx(valueLocalStorage);
			return;
		}
		setCurrentTabIdx(0);
	};
	useEffect(() => {
		initCurrentTabIdx();
	}, []);
	useEffect(() => {
		if (currentTabIdx < 0) return;
		changeDataPaginator(1);
	}, [currentTabIdx]);
	//#endregion
	//#region Functions
	const resetUI = () => {
		filters.clear();
		acadReinforceList.triggerFill();
	};
	const changeDataPaginator = value => {
		paginator.movePage(value);
		acadReinforceList.triggerFill();
	};
	const toggleShowDialogDeleteAcadReinforce = (value = null) => {
		setShowDialogDeleteAcadReinforce(value !== null ? value : false);
	};
	const deleteAcadReinforce = async () => {
		setShowDialogDeleteAcadReinforce(false);
		const success = await requestDeleteAcadReinforceById(
			currentAcadReinforceIdForAction
		);
		if (!success) {
			mainContext.setShowDialogUnexpectedError(true);
			return;
		}
		await acadReinforceList.fill();
		toastMessageSuccess.show(
			"Hemos eliminado tu refuerzo. Los refuerzos creados los podrás visualizar en este listado."
		);
	};
	const canShowPaginator = () => {
		if (
			!titleTabViews ||
			!Object.values(tabData.values).includes(currentTabIdx)
		)
			return false;
		const quantity = titleTabViews[tabData.fields[currentTabIdx]];
		return quantity > RECORDS_BY_PAGE;
	};
	const inUnprocessedTab = currentTabIdx === tabData.values?.UNPROCESSED;
	const getHeaders = () => {
		if (isTutorUser) return HEADERS_TABLE_LIST.TUTOR;
		return inUnprocessedTab
			? [
					...HEADERS_TABLE_LIST.ADMIN_WITH_STATE,
					{
						control: (
							<InSyncOfAcadReinforceUnprocessedButton
								noRecords={acadReinforceList.value.length === 0}
								toSynchronizeFromTheListingRequest={
									acadReinforceList.toSynchronize
								}
								{...syncOfAcadReinforceUnprocessed}
							/>
						),
					},
			  ]
			: HEADERS_TABLE_LIST.ADMIN;
	};
	const openDialogDeleteAcadReinforce = acadReinforceId => {
		setShowDialogDeleteAcadReinforce(true);
		setCurrentAcadReinforceIdForAction(acadReinforceId);
	};
	const openDialogUpdateMassiveAndPendingAcadReinforce = acadReinforceId => {
		dialogShowUpdateMassiveAndPendingAcadReinforce.set(true);
		setCurrentAcadReinforceIdForAction(acadReinforceId);
	};
	//#endregion
	const showNotificationNoContent =
		acadReinforceList.value.length === 0 && loadingRequest.value.hidden;
	if (acadReinforceList.fatalError) return null;
	const headers = getHeaders();
	return (
		<Container>
			<Preloader {...loadingRequest.value} />
			<ListHeader
				loadingRequest={loadingRequest}
				isTutorUser={isTutorUser}
				data={dataRelatedToFilters}
				filters={filters}
				resetUI={resetUI}
				triggerFillAcadReinforceList={acadReinforceList.triggerFill}
				currentTabIdx={currentTabIdx}
				paginator={paginator}
				inSyncOfAcadReinforceUnprocessed={
					syncOfAcadReinforceUnprocessed.inSync
				}
			/>
			<CustomTabs
				viewTabTitles={getViewTabTitles({
					quantities: titleTabViews,
					isTutorUser,
				})}
				currentTabIdx={currentTabIdx}
				changeCurrentTabIdx={idx => setCurrentTabIdx(idx)}
				disabled={syncOfAcadReinforceUnprocessed.inSync}
			/>
			<CustomTableList headers={headers}>
				{loadingRequest.value.hidden
					? acadReinforceList.value.map((item, idx) => (
							<ListItem
								key={idx}
								{...item}
								userRole={userRole}
								currentTabIdx={currentTabIdx}
								tabData={tabData}
								isTutorUser={isTutorUser}
								openDialogUpdateMassiveAndPendingAcadReinforce={
									openDialogUpdateMassiveAndPendingAcadReinforce
								}
								openDialogDeleteAcadReinforce={
									openDialogDeleteAcadReinforce
								}
								inSyncOfAcadReinforceUnprocessed={
									syncOfAcadReinforceUnprocessed.inSync
								}
							/>
					  ))
					: Array(4)
							.fill(0)
							.map((_, idx) => {
								return (
									<SkelletonLoader key={idx}>
										{headers.map((_, idx2) => {
											return (
												<td key={idx2}>
													<div></div>
												</td>
											);
										})}
									</SkelletonLoader>
								);
							})}
			</CustomTableList>
			{canShowPaginator() && (
				<Paginator
					data={paginator.data}
					changeData={changeDataPaginator}
					alignSelf="flex-end"
				/>
			)}
			{showNotificationNoContent && (
				<NotificationNoContent
					title="No tienes refuerzos en esta fecha seleccionada"
					description={`Empieza creando más refuerzos seleccionando "Crear refuerzo"`}
					variant="secondary"
					marginTop="60px"
				/>
			)}
			{inUnprocessedTab && (
				<DialogUpdateMassiveAndPendingAcadReinforce
					loadingRequest={loadingRequest}
					toastMessageSuccess={toastMessageSuccess}
					dialogShow={dialogShowUpdateMassiveAndPendingAcadReinforce}
					fillAcadReinforceList={acadReinforceList.fill}
					acadReinforceId={currentAcadReinforceIdForAction}
				/>
			)}
			<DialogAlertAction
				widthContent="90%"
				show={showDialogDeleteAcadReinforce}
				toggleShow={toggleShowDialogDeleteAcadReinforce}
				title={
					currentTabIdx === 3
						? "¿Deseas eliminar este borrador?"
						: "¿Deseas eliminar este refuerzo?"
				}
				content="Recuerda que si eliminas el refuerzo ya no saldrá en la lista."
			>
				<CustomButton
					variant="quintinary"
					text="Sí, eliminar"
					onClick={deleteAcadReinforce}
				/>
				<CustomButton
					variant="quaternary"
					text="No, cancelar"
					onClick={() => toggleShowDialogDeleteAcadReinforce()}
				/>
			</DialogAlertAction>
			{inUnprocessedTab && (
				<>
					<SyncAcadReinforceUnprocessedDialog
						fillAcadReinforceList={acadReinforceList.fill}
						syncOfAcadReinforceUnprocessed={syncOfAcadReinforceUnprocessed}
					/>
					<DialogNotificationLeaveViewInSync
						canShowDialogLeavingView={canShowDialogLeavingView}
						content={
						<p className="paragraph center">
							Recuerda que al salir, no se actualizarán correctamente los estados de los refuerzos <b>no procesados.</b>
						</p>}
					/>
				</>
			)}
		</Container>
	);
};

export default ListComponent;
